import { ChangeEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SvgIcon } from '@progress/kendo-react-common';
import { chevronRightIcon, plusIcon, uploadIcon } from '@progress/kendo-svg-icons';
import type { DlpLabelModel } from 'services/DlpConfiguration/DlpConfigurationService';
import { groupDlpLabels, labelParent, labelRemovalIndices, moveDlpLabel, renameDlpLabel, validateDlpLabels } from 'utils/dlpLabelHierarchy';
import { LabelImportError, readLabelsFromFile } from 'utils/dlpLabelImport';
import { ConfirmDialog } from '../Common/ConfirmDialog';
import { LabelEditorRow } from './LabelEditorRow';

interface LabelListEditorProps {
    labels: DlpLabelModel[];
    projectId?: string;
    readOnly?: boolean;
    onChange: (labels: DlpLabelModel[]) => void;
}
const PAGE_SIZE = 50;
const PREFIX = 'dlpConfiguration.project.labelHierarchy';

export function LabelListEditor({ labels, projectId, onChange, readOnly = false }: LabelListEditorProps) {
    const { t } = useTranslation();
    const regionId = useId();
    const fileInput = useRef<HTMLInputElement>(null);
    const labelsRef = useRef(labels);
    labelsRef.current = labels;
    const importVersion = useRef(0);
    useEffect(() => () => { importVersion.current++; }, []);
    const [expanded, setExpanded] = useState(false);
    const [openGroup, setOpenGroup] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [childPages, setChildPages] = useState<Record<number, number>>({});
    const [unresolvedPage, setUnresolvedPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [importError, setImportError] = useState<LabelImportError | null>(null);
    const [pending, setPending] = useState<{ labels: DlpLabelModel[]; fingerprint: string } | null>(null);
    const [removeIndex, setRemoveIndex] = useState<number | null>(null);
    const [status, setStatus] = useState<string | null>(null);
    useEffect(() => {
        if (readOnly) {
            importVersion.current++;
            setPending(null);
            setRemoveIndex(null);
            setLoading(false);
        }
    }, [readOnly]);
    const hierarchy = useMemo(() => groupDlpLabels(labels), [labels]);
    const issues = useMemo(() => validateDlpLabels(labels), [labels]);
    const idCounts = useMemo(() => {
        const counts = new Map<string, number>();
        labels.forEach(label => counts.set(label.id, (counts.get(label.id) ?? 0) + 1));
        return counts;
    }, [labels]);
    const childCounts = useMemo(() => {
        const counts = new Map<string, number>();
        labels.forEach(label => { const parent = labelParent(label); if (parent !== null) counts.set(parent, (counts.get(parent) ?? 0) + 1); });
        return counts;
    }, [labels]);
    const parents = hierarchy.groups.map(group => labels[group.rootIndex]).filter(label => label.id.trim() && idCounts.get(label.id) === 1);
    const term = search.trim().toLowerCase();
    const matches = (label: DlpLabelModel) => `${label.id} ${label.name} ${label.parent_id ?? ''}`.toLowerCase().includes(term);
    const groups = hierarchy.groups.filter(group => !term || matches(labels[group.rootIndex]) || group.childIndices.some(i => matches(labels[i])));
    const currentPage = Math.min(page, Math.max(0, Math.ceil(groups.length / PAGE_SIZE) - 1));
    const visibleGroups = groups.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
    const unresolved = hierarchy.unresolved.filter(i => matches(labels[i]));
    const invalidPage = Math.min(unresolvedPage, Math.max(0, Math.ceil(unresolved.length / PAGE_SIZE) - 1));
    const removal = removeIndex === null ? new Set<number>() : labelRemovalIndices(labels, removeIndex);

    const add = (parentId: string | null = null) => {
        setExpanded(true);
        setSearch('');
        if (parentId === null) {
            setPage(Math.floor(hierarchy.groups.length / PAGE_SIZE));
            setOpenGroup(labels.length);
        } else {
            setOpenGroup(labels.findIndex(label => label.id === parentId));
            setChildPages(previous => ({ ...previous, [labels.findIndex(label => label.id === parentId)]: Math.floor((childCounts.get(parentId) ?? 0) / PAGE_SIZE) }));
        }
        onChange([...labels, { id: '', name: '', parent_id: parentId }]);
    };
    const remove = (index: number) => {
        const affected = labelRemovalIndices(labels, index);
        if (affected.size > 1) setRemoveIndex(index);
        else onChange(labels.filter((_, i) => i !== index));
    };
    const importFile = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        event.currentTarget.value = '';
        if (!file) return;
        const version = ++importVersion.current;
        const fingerprint = JSON.stringify(labelsRef.current);
        setLoading(true);
        setPending(null);
        setImportError(null);
        setStatus(null);
        try {
            const imported = await readLabelsFromFile(file, projectId);
            if (version !== importVersion.current) return;
            if (JSON.stringify(labelsRef.current) !== fingerprint) throw new LabelImportError('changed');
            setPending({ labels: imported, fingerprint });
        } catch (error) {
            if (version === importVersion.current) setImportError(error instanceof LabelImportError ? error : new LabelImportError('invalidFile'));
        } finally {
            if (version === importVersion.current) setLoading(false);
        }
    };
    const applyImport = () => {
        if (!pending) return;
        if (JSON.stringify(labelsRef.current) !== pending.fingerprint) {
            setImportError(new LabelImportError('changed'));
            setPending(null);
            return;
        }
        onChange(pending.labels);
        setStatus(t(`${PREFIX}.imported`, { count: pending.labels.length }));
        setPending(null);
        setExpanded(true);
        setOpenGroup(null);
        setSearch('');
        setPage(0);
        setChildPages({});
        setUnresolvedPage(0);
    };
    const row = (index: number) => {
        const label = labels[index];
        const siblings = labels.flatMap((other, i) => labelParent(other) === labelParent(label) ? [i] : []);
        const position = siblings.indexOf(index);
        return <LabelEditorRow key={index} label={label} index={index} parents={parents} readOnly={readOnly}
            hasChildren={Boolean(label.id && childCounts.get(label.id))} canMoveUp={position > 0} canMoveDown={position < siblings.length - 1}
            onMove={direction => {
                onChange(moveDlpLabel(labels, index, direction));
                if (labelParent(label) === null) { setOpenGroup(null); setPage(Math.floor((position + direction) / PAGE_SIZE)); }
            }}
            onUpdate={patch => onChange(renameDlpLabel(labels, index, patch))} onRemove={() => remove(index)} />;
    };
    const pager = (count: number, pageNumber: number, change: (value: number) => void) => count > PAGE_SIZE && (
        <div className="label-pagination">
            <button type="button" className="lm-btn-secondary sm" disabled={pageNumber === 0} onClick={() => change(pageNumber - 1)}>{t(`${PREFIX}.previous`)}</button>
            <span>{t(`${PREFIX}.pageRange`, { from: pageNumber * PAGE_SIZE + 1, to: Math.min(count, (pageNumber + 1) * PAGE_SIZE), count })}</span>
            <button type="button" className="lm-btn-secondary sm" disabled={(pageNumber + 1) * PAGE_SIZE >= count} onClick={() => change(pageNumber + 1)}>{t(`${PREFIX}.next`)}</button>
        </div>
    );

    return (
        <div className="label-vocabulary">
            <div className="label-vocabulary-toolbar">
                <button type="button" className="label-vocabulary-toggle" aria-expanded={expanded} aria-controls={regionId} onClick={() => setExpanded(value => !value)}>
                    <SvgIcon icon={chevronRightIcon} size="small" className={expanded ? 'rotate-90' : ''} />
                    {t(`${PREFIX}.summary`, { count: labels.length, parents: hierarchy.groups.length, children: labels.length - hierarchy.groups.length })}
                </button>
                {!readOnly && <>
                <button type="button" className="lm-btn-secondary sm" onClick={() => add()}><SvgIcon icon={plusIcon} size="small" />{t(`${PREFIX}.addParent`)}</button>
                <button type="button" className="upload-brands-btn" disabled={loading} onClick={() => fileInput.current?.click()}>
                    <SvgIcon icon={uploadIcon} size="small" />{t(loading ? `${PREFIX}.reading` : `${PREFIX}.upload`)}
                </button>
                <input ref={fileInput} style={{ display: 'none' }} type="file" accept=".xlsx" aria-label={t(`${PREFIX}.upload`)} onChange={importFile} disabled={loading} />
                </>}
            </div>
            <div className="dropdown-hint">{t(`${PREFIX}.${readOnly ? 'readOnlyHint' : 'importHint'}`)}</div>
            {status && <div role="status" className="brand-import-result">{status}</div>}
            {importError && <div role="alert" className="brand-import-error">
                {t(`${PREFIX}.importErrors.${importError.code}`)}
                {importError.issues.length > 0 && <ul>{importError.issues.slice(0, 20).map((issue, i) => <li key={i}>
                    {t(`${PREFIX}.row`, { row: issue.row })}: {t(`${PREFIX}.errors.${issue.code}`, { ...issue })}
                </li>)}</ul>}
                {importError.issues.length > 20 && <div>{t(`${PREFIX}.moreErrors`, { count: importError.issues.length - 20 })}</div>}
            </div>}
            {issues.length > 0 && <details className="label-hierarchy-errors">
                <summary>{t(`${PREFIX}.${readOnly ? 'readOnlyInvalidCount' : 'invalidCount'}`, { count: issues.length })}</summary>
                <ul>{issues.slice(0, 20).map((issue, i) => <li key={i}>{t(`${PREFIX}.errors.${issue.code}`, { ...issue })}</li>)}</ul>
            </details>}
            {expanded && <div id={regionId}>
                <input className="lm-input sm label-search" aria-label={t(`${PREFIX}.search`)} placeholder={t(`${PREFIX}.search`)} value={search}
                    onChange={event => { setSearch(event.target.value); setPage(0); setChildPages({}); setUnresolvedPage(0); }} />
                <div className="label-vocabulary-viewport">
                    {visibleGroups.map(group => {
                        const root = labels[group.rootIndex];
                        const open = openGroup === group.rootIndex;
                        const children = group.childIndices.filter(i => !term || matches(root) || matches(labels[i]));
                        const childPage = Math.min(childPages[group.rootIndex] ?? 0, Math.max(0, Math.ceil(children.length / PAGE_SIZE) - 1));
                        return <div className="label-group" key={group.rootIndex}>
                            <button type="button" className="label-group-toggle" aria-expanded={open} onClick={() => setOpenGroup(open ? null : group.rootIndex)}>
                                <SvgIcon icon={chevronRightIcon} size="small" className={open ? 'rotate-90' : ''} />
                                <strong>{root.name || root.id || t(`${PREFIX}.untitled`)}</strong>
                                <span className="label-id">{root.id}</span><span>{t(`${PREFIX}.childCount`, { count: group.childIndices.length })}</span>
                            </button>
                            {open && <div className="label-group-body">
                                {row(group.rootIndex)}
                                <div className="label-children">{children.slice(childPage * PAGE_SIZE, (childPage + 1) * PAGE_SIZE).map(row)}</div>
                                {pager(children.length, childPage, value => setChildPages(previous => ({ ...previous, [group.rootIndex]: value })))}
                                {!readOnly && <button type="button" className="lm-btn-secondary sm" disabled={!root.id.trim() || idCounts.get(root.id) !== 1} onClick={() => add(root.id)}>
                                    <SvgIcon icon={plusIcon} size="small" />{t(`${PREFIX}.addChild`)}
                                </button>}
                            </div>}
                        </div>;
                    })}
                    {hierarchy.unresolved.length > 0 && <details className="label-unresolved">
                        <summary>{t(`${PREFIX}.${readOnly ? 'readOnlyUnresolved' : 'unresolved'}`, { count: hierarchy.unresolved.length })}</summary>
                        {unresolved.slice(invalidPage * PAGE_SIZE, (invalidPage + 1) * PAGE_SIZE).map(row)}
                        {pager(unresolved.length, invalidPage, setUnresolvedPage)}
                    </details>}
                    {!labels.length && <div className="empty-note">{t(`${PREFIX}.${readOnly ? 'readOnlyEmpty' : 'empty'}`)}</div>}
                    {labels.length > 0 && !groups.length && !hierarchy.unresolved.length && <div className="empty-note">{t(`${PREFIX}.noResults`)}</div>}
                </div>
                {pager(groups.length, currentPage, setPage)}
            </div>}
            {!readOnly && pending && <ConfirmDialog title={t(`${PREFIX}.importTitle`)} message={t(`${PREFIX}.importMessage`, { current: labels.length, count: pending.labels.length })}
                confirmLabel={t(`${PREFIX}.applyImport`)} onConfirm={applyImport} onCancel={() => setPending(null)} />}
            {!readOnly && removeIndex !== null && <ConfirmDialog title={t(`${PREFIX}.removeGroupTitle`)} message={t(`${PREFIX}.removeGroupMessage`, { label: labels[removeIndex]?.name || labels[removeIndex]?.id, count: removal.size - 1 })}
                confirmLabel={t('general.remove')} onConfirm={() => { onChange(labels.filter((_, i) => !removal.has(i))); setRemoveIndex(null); setOpenGroup(null); }}
                onCancel={() => setRemoveIndex(null)} danger />}
        </div>
    );
}
